<template>
  <div>
    <h2
      class="mt-0 tw-text-2xl tw-font-semibold tw-mb-2"
      style="color: var(--text-color)"
    >
      Episodes
    </h2>
    <Card>
      <template #header>
        <div
          class="pt-4 px-4 flex flex-column md:flex-row md:items-center gap-3"
        >
          <MultiSelect
            v-model="selectedShows"
            :options="shows"
            option-label="show_title"
            option-value="show_id"
            placeholder="Filter by shows"
            class="w-full md:w-20rem"
            show-clear
            :show-toggle-all="false"
            :maxSelectedLabels="2"
            @change="onShowFilterChange"
          />
          <InputText
            v-model="searchTerm"
            class="w-full md:w-20rem"
            type="text"
            placeholder="Search title or show"
            @input="onSearchInput"
          />
        </div>
      </template>
      <template #content>
        <DataTable
          :value="episodes"
          :paginator="true"
          :rows="rows"
          :first="firstRow"
          :totalRecords="totalEpisodes"
          :sortField="sortField"
          :sortOrder="sortOrder"
          lazy
          :loading="tableLoading"
          class="p-datatable-sm"
          :striped-rows="true"
          @page="onPageChange"
          @sort="onSort"
        >
          <Column
            field="episode_number"
            header="Episode No."
            :sortable="true"
          ></Column>
          <Column field="title" header="Title" :sortable="true"></Column>
          <Column
            field="media_type"
            header="Media Type"
            :sortable="true"
          ></Column>
          <Column header="Edit">
            <template #body="slotProps">
              <div class="flex gap-2 justify-content-center">
                <Button
                  icon="pi pi-eye"
                  outlined
                  rounded
                  severity="help"
                  :loading="loadingEpisodeId === slotProps.data.episode_id"
                  @click="fetchEpisodeDetail(slotProps.data.episode_id)"
                />
              </div>
            </template>
          </Column>
        </DataTable>
      </template>
    </Card>
    <PodcasterEpisodeDetailDialog
      :episode="episodeDetail"
      :open="openEpisodeDetail"
      @close="handleModalClose"
    />
  </div>
</template>

<script setup>
import Card from "primevue/card";
import MultiSelect from "primevue/multiselect";
import DataTable from "primevue/datatable";
import Button from "primevue/button";
import Column from "primevue/column";
import InputText from "primevue/inputtext";
import { onBeforeMount, onBeforeUnmount, ref, computed } from "vue";
import { useStore } from "vuex";
import axios from "axios";
import { API_BASE_URL } from "@/consts";
import { formatTime } from "@/helpers";
import PodcasterEpisodeDetailDialog from "./PodcasterEpisodeDetailDialog.vue";
import { usePaginatedFetcher } from "@/composables/usePaginatedFetcher";

const store = useStore();
const token = localStorage.getItem("podcasterToken");

const loadingEpisodeId = ref("");
const openEpisodeDetail = ref(false);
const selectedShows = ref(["all"]);
const episodeDetail = ref({
  title: "",
  description: "",
  publication_date: "",
  episode_link: "",
  media_url: "",
  media_type: "",
  duration: "",
  image_url: "",
  keywords: "",
  author: "",
});

const handleModalClose = () => {
  openEpisodeDetail.value = false;
};

const shows = computed(() => {
  const tempShowList =
    store.getters.shows?.filter(
      (item) => item.show_title != null && item.show_title != ""
    ) || [];
  return [{ show_id: "all", show_title: "All Shows" }, ...tempShowList];
});

const fetchShows = () => {
  store.dispatch("loadShows", {
    token,
    fields: ["show_id", "show_title", "podcaster_id"],
  });
};

const buildEpisodeParams = () => {
  const filteredShows = (selectedShows.value || []).filter(
    (id) => id !== "all"
  );
  return filteredShows.length
    ? {
        showIds: filteredShows.join(","),
      }
    : {};
};

const {
  items: episodes,
  total: totalEpisodes,
  page,
  rows,
  firstRow,
  sortField,
  sortOrder,
  searchTerm,
  loading: tableLoading,
  fetchPage: fetchEpisodes,
  onPageChange,
  onSort,
  onSearchInput,
  cleanupSearch,
} = usePaginatedFetcher(
  (params) => store.dispatch("loadEpisodes", { token, params }),
  { getExtraParams: buildEpisodeParams, initialRows: 10 }
);

const onShowFilterChange = () => {
  if (!selectedShows.value?.length) {
    selectedShows.value = ["all"];
  } else if (
    selectedShows.value.includes("all") &&
    selectedShows.value.length > 1
  ) {
    selectedShows.value = selectedShows.value.filter((id) => id !== "all");
  }
  page.value = 0;
  fetchEpisodes(0);
};

const fetchEpisodeDetail = async (episodeId) => {
  try {
    loadingEpisodeId.value = episodeId;
    const response = await axios.get(
      API_BASE_URL + "/podcaster/episodes/detail/" + episodeId,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (response.data?.episode) {
      episodeDetail.value = {
        ...episodeDetail.value,
        ...response.data.episode,
        duration: formatTime(response.data.episode.duration || 0),
        publication_date: new Date(
          response.data.episode.publication_date || 0
        ).toLocaleString(),
      };
    }
  } catch (error) {
    console.error("Error fetching episode detail:", error);
  } finally {
    loadingEpisodeId.value = "";
    openEpisodeDetail.value = true;
  }
};

onBeforeMount(() => {
  fetchShows();
  fetchEpisodes();
});

onBeforeUnmount(() => {
  cleanupSearch();
});
</script>
